"use client"

import { useState, useEffect } from "react"
import { getProductsByCategory } from "@/lib/api/dutchie"
import CategoryLayout from "@/components/shop/CategoryLayout"
import type { ProductDisplay } from "@/lib/api/dutchie"

/* ------------------------------------------------------------------ */
/*  Category Configuration                                            */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  "edibles",
  "orals"
]

const DOSAGE_RANGES = [
  { label: "Micro (1-2.5 mg)", test: (d: number) => d >= 1   && d <= 2.5 },
  { label: "Low (2.5-5 mg)",   test: (d: number) => d >  2.5 && d <= 5   },
  { label: "Medium (5-10 mg)", test: (d: number) => d >  5   && d <= 10  },
  { label: "High (10-20 mg)",  test: (d: number) => d >  10  && d <= 20  },
  { label: "Very High (20 mg+)",test: (d: number) => d >  20 }
]

const EFFECTS = [
  "Relaxed","Euphoric","Happy","Sleepy","Creative",
  "Focused","Energetic","Uplifted","Calm","Pain Relief"
]

const SUBCATEGORY_REGEX: [RegExp,string][] = [
  [/mints?|lozenges?|peppermint|spearmint|flintts/i, "Mints & Lozenges"], // keep first! removed word boundaries for better matching
  [/gummies?/i,                "Gummies"],
  [/chocolates?/i,             "Chocolate"],
  [/beverages?|drinks?|tea/i,  "Beverages"],
  [/cookies?|brownies?|baked/i,"Baked Goods"],
  [/capsules?|pills?|tablets?/i,"Capsules"],
  [/tinctures?/i,              "Tinctures"]
]

const titleCase = (s: string) =>
  s.toLowerCase().split(" ").map(w => w[0].toUpperCase()+w.slice(1)).join(" ")

const categorizeEdible = (p: ProductDisplay) => {
  const text = `${p.name ?? ""} ${p.description ?? ""} ${p.subcategory ?? ""}`.toLowerCase()
  
  // Debug logging for Flintts Mints specifically
  if (p.name?.toLowerCase().includes('flintts')) {
    console.log('Categorizing Flintts product:', {
      name: p.name,
      subcategory: p.subcategory,
      searchText: text,
      regexTests: SUBCATEGORY_REGEX.map(([re, label]) => ({
        regex: re.toString(),
        label,
        matches: re.test(text)
      }))
    })
  }
  
  for (const [re, label] of SUBCATEGORY_REGEX) if (re.test(text)) return label
  const fallback = p.subcategory ? titleCase(p.subcategory.replace(/_/g," ")) : "Other"
  return fallback === "Default" ? "Other" : fallback
}

const extractNum = (s?: string) => {
  const m = s?.match(/(\d+(?:\.\d+)?)/); return m ? +m[1] : undefined
}
const dosagePerPiece = (p: ProductDisplay) =>
  extractNum(`${p.name ?? ""} ${p.description ?? ""}`)

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function EdiblesPage() {
  // Custom fetch function that doesn't filter by stock status
  const fetchProducts = async (): Promise<ProductDisplay[]> => {
    try {
      console.log('Fetching products from categories:', CATEGORIES)
      
      // Get all products and filter manually to avoid stock filtering
      const { getProducts } = await import('@/lib/api/dutchie')
      const allProducts = await getProducts()
      
      console.log(`Total products available: ${allProducts.length}`)
      
      // Filter for EDIBLES and ORALS categories (matching the exact category names from API)
      const edibleProducts = allProducts.filter(product => {
        const category = product.category.toUpperCase()
        return category === 'EDIBLES' || category === 'ORALS'
      })
      
      console.log(`Edibles + Orals products found: ${edibleProducts.length}`)
      
      // Deduplicate by ID
      const uniqueProductsMap = new Map<string, ProductDisplay>()
      edibleProducts.forEach(product => {
        if (!uniqueProductsMap.has(product.id)) {
          uniqueProductsMap.set(product.id, product)
        }
      })
      
      const uniqueProducts = Array.from(uniqueProductsMap.values())
      console.log(`Unique products after deduplication: ${uniqueProducts.length}`)
      
      // Extract potency value from product (same logic as pre-rolls/flower page but for mg)
      const extractPotencyValue = (product: ProductDisplay, type: 'thc' | 'cbd'): number => {
        // First try direct fields
        const directValue = type === 'thc' ? product.thc : product.cbd;
        if (directValue) {
          const match = directValue.match(/(\d+(?:\.\d+)?)/);
          if (match && match[1]) {
            return parseFloat(match[1]);
          }
        }

        // Then try potency objects (edibles use mg not %)
        const potencyObj = type === 'thc' ? (product as any).potencyThc : (product as any).potencyCbd;
        if (potencyObj && potencyObj.formatted) {
          const match = potencyObj.formatted.match(/(\d+(?:\.\d+)?)/);
          if (match && match[1]) {
            return parseFloat(match[1]);
          }
        }

        // Also try the range array directly
        if (potencyObj && potencyObj.range && potencyObj.range.length > 0) {
          return potencyObj.range[0];
        }

        // Finally try to extract from text
        const productText = `${product.name || ''} ${product.description || ''}`.toLowerCase();
        const regex = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*mg`, 'i');
        const match = productText.match(regex);

        if (match && match[1]) {
          return parseFloat(match[1]);
        }

        return 0; // Default to 0 if no value found
      }

      // Enhance products with virtual subcategory, dosage, and extracted THC/CBD values
      const enhancedProducts = uniqueProducts.map((product, index) => {
        const thcValue = extractPotencyValue(product, 'thc')
        const cbdValue = extractPotencyValue(product, 'cbd')
        
        // Debug log for first few products
        if (index < 3) {
          console.log('Edibles THC/CBD extraction:', {
            name: product.name,
            potencyThc: (product as any).potencyThc,
            potencyCbd: (product as any).potencyCbd,
            extractedThc: thcValue,
            extractedCbd: cbdValue
          })
        }
        
        return {
          ...product,
          virtualSubcategory: categorizeEdible(product),
          dosage: dosagePerPiece(product),
          // Extract THC/CBD numeric values for filtering (use mg for edibles)
          thc: thcValue.toString() + 'mg',
          cbd: cbdValue.toString() + 'mg'
        }
      })
      
      // Debug subcategory distribution
      const subcounts: Record<string, number> = {}
      enhancedProducts.forEach(product => {
        const sub = (product as any).virtualSubcategory
        subcounts[sub] = (subcounts[sub] || 0) + 1
      })
      console.log('Enhanced products subcategory distribution:', subcounts)
      
      console.log('fetchProducts returning enhanced products:', enhancedProducts.length)
      console.log('Sample enhanced product:', enhancedProducts[0])
      console.log('Enhanced product has virtualSubcategory:', !!enhancedProducts[0]?.virtualSubcategory)
      
      return enhancedProducts
      
    } catch (error) {
      console.error('Error fetching edibles products:', error)
      return []
    }
  }
  
  const config = {
    title: "Edibles",
    blurb: "Premium cannabis-infused edibles for a delightful experience",
    fetch: fetchProducts,
    headerTheme: "orange" as const,
    effects: EFFECTS,
    dosageRanges: DOSAGE_RANGES,
    potencyUnit: "mg",
    showPotency: true,
    filterInStock: false,
    debug: true
  }

  return (
    <CategoryLayout
      config={config}
      productCardProps={{ 
        buttonClassName: "bg-orange-600 hover:bg-orange-700"
      }}
    />
  )
}
